import React, { Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import KpiCard from '../shared/KpiCard';
import constants from '../../../config';
import { CHART_COLORS } from '../shared/formatters';

class AnalyticsSupply extends Component {
  render() {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var data = this.props.data;
    if (!data) return <div className="tw-text-center tw-py-16 tw-text-[#999]">No data available.</div>;

    var sellerGrowth = data.sellerGrowth || [];
    var conditionDist = data.conditionDist || [];
    var sellersByListings = data.sellersByListings || [];
    var topCards = data.topCards || [];

    return (
      <div>
        {/* KPI Cards */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 tw-mb-6">
          <KpiCard icon="fa-store" label="Active Sellers" value={data.activeSellers} color="#D4A843" />
          <KpiCard icon="fa-user-plus" label="New Sellers" value={data.newSellers} color="#10B981" change="in period" />
          <KpiCard icon="fa-user-times" label="Churned Sellers" value={data.churnedSellers} color="#EF4444" change="vs prior period" />
          <KpiCard icon="fa-tag" label="Active Listings" value={data.activeListings} color="#3B82F6" />
          <KpiCard icon="fa-list" label="Avg Per Seller" value={data.avgListings} color="#8B5CF6" />
          <KpiCard icon="fa-truck" label="Fulfillment Rate" value={data.fulfillmentRate + '%'} color="#F59E0B" />
        </div>

        {/* Seller Growth Chart */}
        <div className="tw-rounded-xl tw-p-5 tw-mb-6" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">New Sellers by Day</h3>
          {sellerGrowth.length > 0 ? (
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={sellerGrowth} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sellerGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return v.slice(5); }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                  <Area type="monotone" dataKey="count" name="New Sellers" stroke="#10B981" strokeWidth={2.5} fill="url(#sellerGrad)" dot={false} activeDot={{ r: 5, fill: '#10B981' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No seller data yet</div>
          )}
        </div>

        {/* Row: Condition Dist + Sellers by Listings + Ratings */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          {/* Condition Distribution */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Condition Distribution</h3>
            {conditionDist.length > 0 ? (
              <div>
                <div style={{ width: '100%', height: 160 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={conditionDist} dataKey="count" nameKey="condition" cx="50%" cy="50%" outerRadius={60} innerRadius={35} paddingAngle={2}>
                        {conditionDist.map(function(e, i) { return <Cell key={'cd-' + i} fill={CHART_COLORS[i % CHART_COLORS.length]} />; })}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="tw-space-y-1 tw-mt-2">
                  {conditionDist.map(function(c, i) {
                    return (
                      <div key={'cl-' + i} className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                        <span className="tw-flex tw-items-center tw-gap-2">
                          <span className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-inline-block" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="tw-text-[#666] dark:tw-text-[#aaa]">{c.condition}</span>
                        </span>
                        <span className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{c.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data</div>
            )}
          </div>

          {/* Sellers by Listing Count */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Sellers by Listing Count</h3>
            {sellersByListings.length > 0 ? (
              <div style={{ width: '100%', height: 180 }}>
                <ResponsiveContainer>
                  <BarChart data={sellersByListings} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                    <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#999' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    <Bar dataKey="sellers" name="Sellers" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data</div>
            )}
          </div>

          {/* Seller Ratings */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Seller Ratings</h3>
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-4">
              <div className="tw-text-4xl tw-font-bold" style={{ color: '#D4A843' }}>{data.avgSellerRating}</div>
              <div className="tw-flex tw-items-center tw-gap-1 tw-mt-2">
                {[1,2,3,4,5].map(function(star) {
                  var filled = star <= Math.round(data.avgSellerRating);
                  return <i key={'star-' + star} className={'fa ' + (filled ? 'fa-star' : 'fa-star-o')} style={{ color: '#D4A843', fontSize: 18 }} />;
                })}
              </div>
              <div className="tw-text-sm tw-text-[#999] tw-mt-2">{data.totalReviews} total reviews</div>
            </div>
          </div>
        </div>

        {/* Inventory Depth — Top Cards */}
        <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Inventory Depth — Top 20 Cards</h3>
          {topCards.length > 0 ? (
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-text-sm">
                <thead>
                  <tr className="tw-border-b tw-border-[#d0d0d0] dark:tw-border-[#555]">
                    <th className="tw-text-left tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Card</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Set</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Rarity</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Listings</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Lowest</th>
                  </tr>
                </thead>
                <tbody>
                  {topCards.map(function(card, i) {
                    return (
                      <tr key={'tc-' + i} className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222]">
                        <td className="tw-py-2 tw-px-2">
                          <div className="tw-flex tw-items-center tw-gap-2">
                            {card.image && <img src={card.image.startsWith('http') ? card.image : constants.DOMAIN + '/' + card.image} alt="" className="tw-w-8 tw-h-10 tw-object-contain tw-rounded" />}
                            <div>
                              <div className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{card.name}</div>
                              <div className="tw-text-xs tw-text-[#999]">{card.cardNumber}</div>
                            </div>
                          </div>
                        </td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-text-[#666] dark:tw-text-[#aaa]">{card.cardSet}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-text-[#666] dark:tw-text-[#aaa]">{card.rarity}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{card.listingCount}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold" style={{ color: '#10B981' }}>{card.lowestPrice != null ? '$' + card.lowestPrice.toFixed(2) : '--'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No inventory data</div>
          )}
        </div>
      </div>
    );
  }
}

export default AnalyticsSupply;
